import { ArrowRight, Check, ChevronRight, Plus } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useMemo, useState } from 'react';
import emmaAvatar from '@assets/images/emma.webp';
import isabelAvatar from '@assets/images/isabel.webp';
import naomiAvatar from '@assets/images/naomi-pool.webp';
import sofiaAvatar from '@assets/images/sofia-1.webp';
import pulpWhiteLogo from '@assets/logos/pulp_white.svg';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Flex, IconButton, Text, TextField } from '@radix-ui/themes';
import { Button, FieldError, HeaderItalic, Spinner } from '@/components/common';
import { WizardHeader, WizardScreen } from '@/components/layout';
import { useAuth } from '@/hooks/useAuth';
import { analyticsClient, monitoringClient } from '@/lib';
import { authService, emailService } from '@/services';
import { InviteAlreadyClaimedError, verifyInviteCode } from '@/services/inviteService';

const CHAIN_AVATARS = [sofiaAvatar, naomiAvatar, emmaAvatar, isabelAvatar] as const;
const REDIRECT_DELAY_MS = 1500;

type Translate = (key: string) => string;

const createInviteCodeSchema = (t: Translate) =>
  z.object({
    code: z.string().trim().min(4, t('errors.invalidInviteCode')),
  });

type InviteCodeFormValues = z.infer<ReturnType<typeof createInviteCodeSchema>>;

function authMethod(providerData: readonly { providerId: string }[]): string {
  const ids = providerData.map(p => p.providerId);
  if (ids.includes('google.com')) return 'google';
  if (ids.includes('password')) return 'email_password';
  return ids[0] ?? 'unknown';
}

function codeButtonClassName(validated: boolean, hasInput: boolean): string | undefined {
  if (validated) return 'cot-pop pointer-events-none';
  if (hasInput) {
    return 'shadow-[0_0_14px_color-mix(in_srgb,var(--accent-9)_50%,transparent)]';
  }
  return undefined;
}

function SubmitIcon({ validating, validated }: { validating: boolean; validated: boolean }) {
  if (validating) return <Spinner size="1" />;
  if (validated) return <Check size={16} strokeWidth={1.8} />;
  return <ArrowRight size={18} strokeWidth={1.6} />;
}

const ChainVisualization = () => (
  <Flex align="center" justify="center" className="-mx-2 mb-2">
    {CHAIN_AVATARS.map((src, index) => (
      <Flex key={index} align="center">
        <img
          src={src}
          alt=""
          className="shrink-0 rounded-full object-cover"
          style={{ width: 44, height: 44 }}
          aria-hidden="true"
        />
        <ChevronRight size={10} className="mx-px shrink-0 text-accent" />
      </Flex>
    ))}
    <div
      className="flex shrink-0 flex-col items-center justify-center rounded-full border-2 border-dashed border-white bg-white/6"
      style={{ width: 54, height: 54 }}
      aria-hidden="true"
    >
      <Plus size={16} className="text-white" strokeWidth={1.8} />
      <span className="mt-px font-mono text-[7px] leading-none tracking-widest text-white">
        YOU?
      </span>
    </div>
  </Flex>
);

/** v90 s-invite-code — claim a code after auth, before onboarding. */
export function InviteCodePage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [validated, setValidated] = useState(false);
  const [validating, setValidating] = useState(false);
  const [hasInput, setHasInput] = useState(false);

  const schema = useMemo(() => createInviteCodeSchema(key => t(key)), [t]);
  const {
    control,
    getValues,
    setError: setFieldError,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm<InviteCodeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  const handleCodeChange = (next: string) => {
    setHasInput(next.trim().length > 0);
    setValidated(false);
    clearErrors('code');
  };

  const reportSubmitError = (err: unknown) => {
    if (err instanceof InviteAlreadyClaimedError) {
      setFieldError('code', { message: t('errors.inviteAlreadyClaimed') });
      return;
    }
    if (err instanceof Error) {
      const invalidCode =
        err.message === 'INVALID_INVITE_CODE' || err.message === 'INVITE_CODE_EXPIRED';
      setFieldError('code', { message: invalidCode ? t('errors.invalidInviteCode') : err.message });
      return;
    }
    setFieldError('code', { message: t('errors.generic') });
  };

  const handleSubmit = async () => {
    clearErrors('code');
    const ok = await trigger('code');
    if (!ok || !user || validating) return;

    setValidating(true);
    try {
      const code = getValues('code').trim();
      const isValid = await verifyInviteCode(code);
      if (!isValid) {
        setFieldError('code', { message: t('errors.invalidInviteCode') });
        return;
      }

      // Show the accepted state before claiming: the claim writes
      // inviteCodeUsed, the profile listener swaps route trees, and this
      // page unmounts — which would cut the check animation short.
      setValidated(true);
      setValidating(false);
      await new Promise(resolve => setTimeout(resolve, REDIRECT_DELAY_MS));

      const { type } = await authService.claimInviteCodeForUser(user.uid, code);

      analyticsClient.capture('account_created', {
        source: type === 'user' ? 'user_invite_code' : 'invite_code',
        invite_code_type: type,
        invite_code: code,
        method: authMethod(user.providerData),
        ...(user.metadata.creationTime ? { created_at: user.metadata.creationTime } : {}),
      });

      void emailService.sendWelcomeEmail(user.uid).catch(err => {
        monitoringClient.captureException(err, { context: 'welcome_email' });
      });

      navigate('/pledge');
    } catch (err) {
      setValidated(false);
      reportSubmitError(err);
    } finally {
      setValidating(false);
    }
  };

  return (
    <WizardScreen header={<WizardHeader withLogoutButton />}>
      <Flex direction="column" minHeight="100%" gap="4" px="5" py="3" pb="5">
        <Flex justify="center" pt="1">
          <img className="pulp-logo" src={pulpWhiteLogo} alt="pulp" width={70} />
        </Flex>

        <ChainVisualization />

        <Box style={{ textAlign: 'center' }}>
          <HeaderItalic color="red" align="center" mb="3">
            {t('chainOfTrust.title')}
          </HeaderItalic>
          <Text
            size="2"
            color="gray"
            as="p"
            style={{ lineHeight: 1.55, maxWidth: 280, margin: '0 auto' }}
          >
            {t('chainOfTrust.description')}
          </Text>
        </Box>

        <Flex direction="column" gap="1">
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <Box position="relative">
                <TextField.Root
                  size="3"
                  radius="full"
                  value={field.value}
                  onChange={e => {
                    const next = e.target.value.toUpperCase();
                    field.onChange(next);
                    handleCodeChange(next);
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  placeholder={t('chainOfTrust.placeholder')}
                  autoComplete="off"
                  autoCapitalize="characters"
                  aria-invalid={errors.code ? true : undefined}
                  style={{ textAlign: 'center' }}
                >
                  <TextField.Slot side="right" pr="1">
                    <IconButton
                      type="button"
                      size="2"
                      radius="full"
                      variant="solid"
                      color="red"
                      highContrast={!validated && !hasInput}
                      disabled={!validated && (validating || !hasInput)}
                      onClick={validated ? undefined : () => void handleSubmit()}
                      aria-label={validated ? undefined : t('chainOfTrust.validateAria')}
                      aria-hidden={validated || undefined}
                      tabIndex={validated ? -1 : undefined}
                      className={codeButtonClassName(validated, hasInput)}
                    >
                      <SubmitIcon validating={validating} validated={validated} />
                    </IconButton>
                  </TextField.Slot>
                </TextField.Root>
              </Box>
            )}
          />
          <FieldError message={errors.code?.message} align="center" />
        </Flex>

        <Text
          size="1"
          color={validated ? undefined : 'gray'}
          align="center"
          style={validated ? { color: 'var(--accent-9)' } : undefined}
        >
          {validated ? t('chainOfTrust.accepted') : t('chainOfTrust.help')}
        </Text>

        <Box mt="auto" pt="4">
          <Button
            type="button"
            variant="outline"
            color="gray"
            highContrast
            fullWidth
            size="md"
            onClick={() => {
              analyticsClient.capture('waitlist_link_clicked');
              navigate('/waitlist');
            }}
          >
            {t('chainOfTrust.requestInviteCode')}
          </Button>
        </Box>
      </Flex>
    </WizardScreen>
  );
}
